import { useEffect, useRef, useState } from "react";

/** How long the "this changed" tint runs; matches `optivo-flash` in globals.css. */
export const CHANGE_FLASH_MS = 600;

/**
 * True for a moment after `signature` changes, never on first sight — paired
 * with the `flash-attention` class, on the rail as on a page: the class reads
 * its tint off the surface it stands on (`--sidebar-flash` under `bg-sidebar`).
 *
 * For a corner of the screen the operator is not looking at, the navigation
 * rail above all: when a setting is saved their eye is on the dialog and its
 * toast, when a sync fails they are somewhere else entirely, and a window
 * minimised into the rail has to show where it went. A corner that quietly
 * swaps a few words is a change nobody sees. Arriving on a page is not a
 * change, though — flashing there would train the operator to ignore the one
 * signal that means something.
 */
export function useChangeFlash(signature: string): boolean {
    const previous = useRef(signature);
    const [flashing, setFlashing] = useState(false);

    useEffect(() => {
        if (previous.current === signature) return;
        previous.current = signature;
        setFlashing(true);
        const timer = setTimeout(() => setFlashing(false), CHANGE_FLASH_MS);
        return () => clearTimeout(timer);
    }, [signature]);

    return flashing;
}
